import { useState, useEffect, useCallback } from 'react'

/**
 * useState avec persistance dans localStorage.
 * La clé doit être unique dans l'application.
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T,
): [T, (v: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key)
      return raw !== null ? (JSON.parse(raw) as T) : defaultValue
    } catch {
      return defaultValue
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Quota dépassé ou mode privé — silencieux
    }
  }, [key, value])

  const set = useCallback((v: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = typeof v === 'function' ? (v as (p: T) => T)(prev) : v
      return next
    })
  }, [])

  return [value, set]
}

/** Supprime toutes les clés cc-* du localStorage */
export function resetPersistedPreferences(): void {
  const keys = Object.keys(localStorage).filter((k) => k.startsWith('cc-'))
  keys.forEach((k) => localStorage.removeItem(k))
}
